import { ChevronDown, Clock, Download, Package } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  downloadPluginVersion,
  getPluginVersions,
  getPlugins,
  parseApiError,
} from '../../helpers/licenseApi';
import { formatDate } from './adminUtils';
import { formatBytes } from './licenseUtils';

const TYPE_LABEL = { core: 'Core', addon: 'Add-on', theme: 'Theme' };

// Sanitize the per-version changelog HTML and strip GitHub's auto-generated
// "Full Changelog: …/compare/vA...vB" footer, which we don't want to surface.
const cleanChangelog = (raw) => {
  // Drop the markdown form first, in case the source isn't fully HTML.
  const stripped = (raw || '').replace(/\*\*full changelog\*\*:\s*\S+/gi, '');
  const clean = DOMPurify.sanitize(stripped);
  if (!clean) return '';
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  // Remove the whole line wrapping a compare link, or any line that names it.
  doc.querySelectorAll('a[href*="/compare/"]').forEach((a) => {
    (a.closest('p, li') || a).remove();
  });
  doc.querySelectorAll('p, li').forEach((el) => {
    if (/full changelog/i.test(el.textContent)) el.remove();
  });
  return doc.body.innerHTML.trim();
};

// Core first, then addons, then themes — stable, sensible default order.
const TYPE_ORDER = { core: 0, addon: 1, theme: 2 };
const sortPlugins = (plugins) =>
  [...plugins].sort((a, b) => (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9));

function VersionTag({ tag }) {
  if (!tag) return null;
  return <span className={`sh-vh-tag ${tag}`}>{tag}</span>;
}

// The expandable per-plugin version archive. Versions are fetched lazily the
// first time the drawer opens.
function VersionHistory({ slug }) {
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingVersion, setDownloadingVersion] = useState(null);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getPluginVersions(slug);
        if (active) setVersions(data);
      } catch (requestError) {
        if (active) setError(parseApiError(requestError, 'Could not load version history.'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  const onDownload = useCallback(
    async (version) => {
      setDownloadError('');
      setDownloadingVersion(version.version);
      try {
        await downloadPluginVersion(version.download_endpoint, `${slug}-${version.version}.zip`);
      } catch (requestError) {
        setDownloadError(parseApiError(requestError, 'Download failed. Try again.'));
      } finally {
        setDownloadingVersion(null);
      }
    },
    [slug],
  );

  if (loading) return <div className="sh-vh-loading">Loading versions…</div>;
  if (error) return <div className="error-banner sh-vh-error">{error}</div>;
  if (!versions || !versions.length) return <div className="sh-vh-loading">No versions yet.</div>;

  return (
    <div className="sh-version-history">
      <div className="sh-vh-caption">
        <Clock size={13} />
        Version archive — download any prior build
      </div>
      {downloadError ? <div className="error-banner sh-vh-error">{downloadError}</div> : null}
      {versions.map((version) => {
        const busy = downloadingVersion === version.version;
        const disabled = !version.available || busy;
        return (
          <div className="sh-vh-row" key={version.version}>
            <span className="sh-v-num">
              v{version.version}
              <VersionTag tag={version.tag} />
            </span>
            <span className="sh-v-date">{formatDate(version.released_at)}</span>
            <span
              className="sh-v-sum"
              // eslint-disable-next-line react/no-danger -- sanitized in cleanChangelog
              dangerouslySetInnerHTML={{ __html: cleanChangelog(version.changelog) }}
            />
            <span className="sh-v-meta">{formatBytes(version.file_size)}</span>
            <span className="sh-v-act">
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={() => onDownload(version)}
                disabled={disabled}
                title={
                  version.available
                    ? `Download v${version.version} (.zip)`
                    : 'This build is not available for download'
                }
              >
                <Download size={13} />
                {busy ? 'Downloading…' : '.zip'}
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PluginRow({ plugin, open, onToggle }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const icon = plugin.icons?.['2x'] || plugin.icons?.['1x'] || null;

  const onDownloadLatest = async () => {
    setDownloadError('');
    setDownloading(true);
    try {
      const endpoint = `/api/v1/plugins/${encodeURIComponent(plugin.slug)}/versions/${encodeURIComponent(
        plugin.current_version,
      )}/download`;
      await downloadPluginVersion(endpoint, `${plugin.slug}-${plugin.current_version}.zip`);
    } catch (requestError) {
      setDownloadError(parseApiError(requestError, 'Download failed. Try again.'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`sh-plugin-group ${open ? 'open' : ''}`}>
      <div className="sh-plugin-row">
        <div className="sh-plugin-main">
          <div className="sh-plugin-thumb">
            {icon ? <img src={icon} alt="" /> : <Package size={18} />}
          </div>
          <div className="sh-plugin-info">
            <span className="sh-nm">
              {plugin.name}
              {plugin.type === 'core' ? <span className="sh-core-tag">Core</span> : null}
            </span>
            <span className="sh-ds">{TYPE_LABEL[plugin.type] || plugin.type}</span>
          </div>
        </div>
        <div className="sh-ver-cell">
          <span className="sh-ver-pill">v{plugin.current_version}</span>
          <span className="sh-rel">Latest</span>
        </div>
        <div className="sh-plugin-actions">
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={onDownloadLatest}
            disabled={downloading}
          >
            <Download size={14} />
            {downloading ? 'Downloading…' : 'Download .zip'}
          </button>
          <button
            className="sh-btn-hist"
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} version history for ${plugin.name}`}
          >
            <ChevronDown className="sh-chev" size={16} />
          </button>
        </div>
      </div>
      {downloadError ? <div className="error-banner sh-vh-error">{downloadError}</div> : null}
      {open ? <VersionHistory slug={plugin.slug} /> : null}
    </div>
  );
}

export default function PluginsCard() {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getPlugins();
        if (!active) return;
        setPlugins(sortPlugins(data));
      } catch (requestError) {
        if (active) setError(parseApiError(requestError, 'Could not load your plugins.'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const toggle = (slug) => setOpen((prev) => ({ ...prev, [slug]: !prev[slug] }));

  return (
    <section className="panel-card sh-plugins-card">
      <div className="card-header">
        <h2>Plugins &amp; add-ons</h2>
        {plugins.length ? <span className="sh-count">{plugins.length} available</span> : null}
      </div>

      {loading ? <div className="loading-block">Loading plugins</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {!loading && !error && !plugins.length ? (
        <div className="sh-empty">
          <Package size={28} />
          <h3>No active license</h3>
          <p>
            You don&apos;t have an active license, so there are no plugins to download yet. Activate or
            purchase a license to unlock the catalog.
          </p>
        </div>
      ) : null}

      {!loading && plugins.length ? (
        <div className="sh-plugin-list">
          {plugins.map((plugin) => (
            <PluginRow
              key={plugin.slug}
              plugin={plugin}
              open={!!open[plugin.slug]}
              onToggle={() => toggle(plugin.slug)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
